import type { ReactNode } from 'react';

import { Card, MonoLabel } from '@/design-system';
import type { Wallet } from '@/entities/credit';

import { billingCopy, formatKeuringen } from '../copy';

export interface WalletCardProps {
    wallet: Wallet;
}

/**
 * Tegoed (scherm 10, rechterkolom), in hele keuringen.
 *
 * Vooraan wat je nog kunt besteden, daaronder hoe dat is opgebouwd - de
 * inbegrepen keuringen van deze abonnementsmaand (die aan het eind ervan
 * vervallen) en de gekochte (die nooit vervallen) - en wat er gereserveerd is.
 * En net zo belangrijk: wat er gebeurt als het op is. Keuringen koop je bij
 * Keuringen kopen; een tegoedpakket bestaat niet meer.
 */
export function WalletCard({ wallet }: WalletCardProps) {
    return (
        <Card padding="md" as="section" aria-labelledby="wallet-title">
            <h2
                id="wallet-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {billingCopy.walletTitle}
            </h2>

            <div className="mt-3">
                <MonoLabel size={10} tone="muted">
                    {billingCopy.spendable}
                </MonoLabel>
            </div>
            <p className="m-0 mt-[6px] font-[family-name:var(--font-mono)] text-[28px] leading-[1.1] font-medium text-[var(--text-primary)]">
                {formatKeuringen(wallet.spendable)}
            </p>

            <dl className="m-0 mt-3 border-t border-[var(--border-subtle-2)] pt-2">
                <Row label={billingCopy.balance} value={formatKeuringen(wallet.balance)} />
                {wallet.includedBalance > 0 ? (
                    <>
                        <Row
                            label={billingCopy.includedLeft}
                            value={formatKeuringen(wallet.includedBalance)}
                        />
                        <Row
                            label={billingCopy.purchasedLeft}
                            value={formatKeuringen(wallet.purchasedBalance)}
                        />
                    </>
                ) : null}
                <Row label={billingCopy.reserved} value={formatKeuringen(wallet.reserved)} />
                {wallet.graceCredits > 0 ? (
                    <Row label={billingCopy.grace} value={formatKeuringen(wallet.graceCredits)} />
                ) : null}
                <Row
                    label={billingCopy.threshold}
                    value={
                        wallet.lowBalanceThreshold > 0
                            ? formatKeuringen(wallet.lowBalanceThreshold)
                            : billingCopy.thresholdOff
                    }
                />
            </dl>

            <p className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {wallet.reserved > 0 ? `${billingCopy.reservedHelp} ` : ''}
                {!wallet.blockOnEmpty
                    ? billingCopy.noBlock
                    : wallet.graceCredits > 0
                      ? billingCopy.blockAfterGrace(formatKeuringen(wallet.graceCredits))
                      : billingCopy.blockOnEmpty}
            </p>
        </Card>
    );
}

function Row({ label, value }: { label: ReactNode; value: string }) {
    return (
        <div className="flex items-center justify-between gap-3 py-[5px]">
            <dt className="text-[13px] leading-[1.4] text-[var(--text-secondary)]">{label}</dt>
            <dd className="m-0 text-right font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-primary)]">
                {value}
            </dd>
        </div>
    );
}
